import { useMutation } from '@apollo/client/react'

import { graphql } from '~publish/graphql'

import { GetTeamMemberCounts } from './useTeamMemberCounts'
import { GetTeamMembers, TEAM_MEMBERS_PAGE_SIZE } from './useTeamMembers'

export const UpdateTeamMemberRole = graphql(/* GraphQL */ `
  mutation UpdateTeamMemberRole($input: UpdateTeamMemberRoleInput!) {
    updateTeamMemberRole(input: $input) {
      ... on UpdateTeamMemberRoleSuccess {
        teamMember {
          id
          name
          email
          organizationRole
          isLocked
          isOwner
        }
      }
      ... on LastAdminError {
        message
      }
      ... on NotFoundError {
        message
      }
    }
  }
`)

interface UpdateTeamMemberRoleInput {
  organizationId: string
  accountId: string
  organizationRole: 'admin' | 'member'
}

interface UseUpdateTeamMemberRoleReturn {
  updateTeamMemberRole: (input: UpdateTeamMemberRoleInput) => Promise<{
    success: boolean
    error?: string
  }>
  loading: boolean
}

export const useUpdateTeamMemberRole = (): UseUpdateTeamMemberRoleReturn => {
  const [mutate, { loading }] = useMutation(UpdateTeamMemberRole)

  const updateTeamMemberRole = async (
    input: UpdateTeamMemberRoleInput,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data } = await mutate({
        variables: { input },
        refetchQueries: [
          {
            query: GetTeamMembers,
            variables: {
              input: {
                organizationId: input.organizationId,
                first: TEAM_MEMBERS_PAGE_SIZE,
              },
            },
          },
          // Refresh the Roles summary cards. Passed as a document, not
          // { query }, so it only refetches while RolesTeam has it mounted:
          // legacy organizations share this hook and never issue it.
          GetTeamMemberCounts,
        ],
      })

      if (!data?.updateTeamMemberRole) {
        return { success: false, error: 'Failed to update team member role' }
      }

      const result = data.updateTeamMemberRole

      if (result.__typename === 'UpdateTeamMemberRoleSuccess') {
        return { success: true }
      }

      // Handle error responses
      if (
        result.__typename === 'LastAdminError' ||
        result.__typename === 'NotFoundError'
      ) {
        return { success: false, error: result.message }
      }

      return { success: false, error: 'Unknown error occurred' }
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'An error occurred',
      }
    }
  }

  return {
    updateTeamMemberRole,
    loading,
  }
}
